import { useEffect } from 'react';

import { SubmitButton } from '@capsule/consumer-design-system';

import { FieldArray, Form, withFormik } from 'formik';
import { connect } from 'react-redux';
import { compose } from 'recompact';
import { css } from 'styled-components';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { Typography } from 'src/app/components/Elements/Typography';
import { Step } from 'src/app/components/Pages/GetStarted/StepLayout/Step';
import { MedicationsFieldArray } from 'src/app/components/PageSections/DoctorTransferForm/MedicationsFieldArray/MedicationsFieldArray';
import { filterMedicationFormValues } from 'src/app/components/PageSections/DoctorTransferForm/utils';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { AccountPageAddDependent } from 'src/app/router/paths';
import { searchForMedications, setOnboardingFlow, setOnboardingFormValues, segmentTrack } from 'src/app/state/actions';
import { SET_ONBOARDING_MEDICATIONS, SET_SHOULD_TRANSFER_ALL_MEDS } from 'src/app/state/actions/types';
import { getOnboardingForm, getPatients, isLoggedIn } from 'src/app/state/selectors';
import { useSelector } from 'src/app/state/store';
import type { Dispatch, RootState } from 'src/app/state/store';
import type { Medication } from 'src/app/types';
import { FORM_INPUT_ICON_STRINGS } from 'src/consts';

import { Checkbox } from '../../../LoggedInTransfer/Experiment/Medication/styles';
import { FLOW } from '../../consts';
import { StyledSubtitle } from '../../styles';

import { FamilyButton, FamilyButtonContainer, InputContainer, MedStep } from './styles';
import { validation } from './validations';

const UnconnectedMedicationsStep = ({
  dispatchSegmentTrack,
  handleSubmit,
  isSubmitting,
  isValid,
  setFieldValue,
  setOnboardingMedications,
  setShowHorizontalProgressBar,
  values,
  zipCode,
  shouldShowTransferFormFullProfileTransferInExperience,
  setShouldTransferAllMeds,
}) => {
  const isUserLoggedIn = useSelector(isLoggedIn);
  const onboardingFormValues = useSelector(getOnboardingForm);
  const isFromPharmacy = !onboardingFormValues.isDoctorTransfer;
  const patients = useSelector(getPatients);
  const hasManyPatients = patients && patients.length > 1;
  const dispatchSelectAllMedsEvent = useSegmentEvent('selectAllMeds', { shouldTrackOnMount: false });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (isUserLoggedIn) {
      dispatchSegmentTrack(UNIFIED.viewLoggedInRxInfo, {});
    } else {
      dispatchSegmentTrack(UNIFIED.viewOnboardingRxInfo, {});
    }
  }, [isUserLoggedIn]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    setShowHorizontalProgressBar(true);
  }, [setShowHorizontalProgressBar]);

  /*
   * Set onboarding medications values when formik values change, ie. when users select a medication.
   */
  useEffect(() => {
    setOnboardingMedications(values.medications);
    setShouldTransferAllMeds(values.shouldTransferAllMeds);
  }, [setOnboardingMedications, setShouldTransferAllMeds, values]);

  const saveMedications = () => {
    setOnboardingMedications(values.medications);
  };

  const stepTitle = `Which medications should we ${
    isFromPharmacy ? `transfer from your pharmacy` : `get from your doctor`
  }?`;

  return (
    <MedStep id="med-step">
      <Step
        title={stepTitle}
        text={<StyledSubtitle>We’ll contact them on your behalf</StyledSubtitle>}
        customHandlePrevText={'Back'}
      >
        <Form onSubmit={handleSubmit}>
          <div
            onKeyDown={e => {
              if (e.key === 'Enter' && isValid) {
                handleSubmit();
              }
            }}
          >
            {isFromPharmacy && shouldShowTransferFormFullProfileTransferInExperience && (
              <Checkbox
                name="shouldTransferAllMeds"
                label={{ content: 'Transfer all medications', isHidden: false }}
                defaultChecked={values.shouldTransferAllMeds}
                onChange={() => {
                  // reverse the value of shouldTransferAllMeds since this evals before the value is updated
                  if (!values.shouldTransferAllMeds) {
                    dispatchSelectAllMedsEvent();
                  }
                }}
              />
            )}
            {!values?.shouldTransferAllMeds && (
              <FieldArray
                name="medications"
                render={arrayHelpers => {
                  return (
                    <InputContainer>
                      <MedicationsFieldArray
                        medications={values.medications}
                        fieldArrayName="medications"
                        medicationsArrayHelpers={arrayHelpers}
                        setFieldValue={setFieldValue}
                        saveMedications={saveMedications}
                        iconString={FORM_INPUT_ICON_STRINGS.system_search}
                        disabled={false}
                        zipCode={zipCode}
                        isTransferForm
                      />
                    </InputContainer>
                  );
                }}
              />
            )}
          </div>

          <SubmitButton
            variant="contained-secondary-lg"
            isSubmitting={isSubmitting}
            css={css`
              margin-top: ${({ theme }) => theme.space.s5};
            `}
          >
            Continue
          </SubmitButton>
        </Form>

        {isUserLoggedIn && !hasManyPatients && (
          <FamilyButtonContainer>
            <Typography variant="body">Ordering for someone else?</Typography>
            <FamilyButton to={AccountPageAddDependent.toPath()} size="medium" style="tertiary" block>
              + Add A Family Member
            </FamilyButton>
          </FamilyButtonContainer>
        )}
      </Step>
    </MedStep>
  );
};

const ConnectedMedicationsStep = connect(
  (state: RootState) => {
    return {
      medications: state.onboardingForm.medications,
      zipCode: state.onboardingForm.zipCode,
      isNoRxTransferFlow: state.onboardingForm.isNoRxTransferFlow,
      allMedsTransfer: state.onboardingForm.shouldTransferAllMeds,
    };
  },
  (dispatch: Dispatch) => ({
    setOnboardingMedications: medications => dispatch({ type: SET_ONBOARDING_MEDICATIONS, medications }),
    dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
    searchMedications: async (searchTerm, zipCode) => {
      const res = await dispatch(searchForMedications(searchTerm, zipCode));
      return res.medications;
    },
    onSetOnboardingFormValues: values => dispatch(setOnboardingFormValues(values)),
    onSetOnboardingFlow: (flow: FLOW) => dispatch(setOnboardingFlow(flow)),
    setShouldTransferAllMeds: (shouldTransferAllMeds: boolean) => {
      dispatch({ type: SET_SHOULD_TRANSFER_ALL_MEDS, shouldTransferAllMeds });
    },
  })
);

const enhancedWithFormik = withFormik({
  mapPropsToValues: ({ medications }) => ({ medications }),
  validationSchema: validation,
  validateOnChange: true,
  validateOnBlur: true,
  validateOnMount: true,
  handleSubmit: ({ medications }, { props }) => {
    const {
      handleNextClick,
      dispatchSegmentTrack,
      setOnboardingMedications,
      isNoRxTransferFlow,
      onSetOnboardingFlow,
      allMedsTransfer,
      pathname,
    } = props;
    if (isNoRxTransferFlow) {
      dispatchSegmentTrack(UNIFIED.skipTransferForm, { pagepath: pathname });
      onSetOnboardingFlow(FLOW.NO_RX);
    } else {
      onSetOnboardingFlow(FLOW.TRANSFER);
    }
    /**
     * The medications array always has an irrelevant first item because it's the actual form
     * state for the input.
     * */
    const medsWithNames = filterMedicationFormValues(medications) as Medication[];

    const validMedications = medsWithNames.filter(({ attributes }) => !!attributes!.doctorRequiredForTransfer);
    setOnboardingMedications(medsWithNames);
    dispatchSegmentTrack(UNIFIED.submitOnboardingMedication, {
      medication_names: validMedications.map(({ attributes }) => attributes!.primaryName),
      all_meds: allMedsTransfer,
    });
    handleNextClick();
  },
});

export const MedicationsStep = compose(ConnectedMedicationsStep, enhancedWithFormik)(UnconnectedMedicationsStep);
